/**
 * Match risk `country` strings to GeoJSON feature names (loose, for counts + filter).
 */

const ALIASES: Record<string, string[]> = {
  usa: ['united states', 'united states of america', 'u.s.a.'],
  uk: ['united kingdom', 'great britain', 'england'],
  uae: ['united arab emirates'],
  'korea, south': ['south korea', 'korea, republic of', 'republic of korea'],
  russia: ['russian federation'],
};

export function normalizeCountry(s: string | undefined | null): string {
  if (!s || !String(s).trim()) return '';
  return String(s)
    .toLowerCase()
    .replace(/[.,']/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Risk country string -> set of normalized tokens we count against GeoJSON names */
export function countryKeysFromRisk(country: string | undefined): string[] {
  const n = normalizeCountry(country);
  if (!n) return [];
  const keys = [n];
  for (const [k, vals] of Object.entries(ALIASES)) {
    if (vals.some((v) => v === n || n.includes(v) || v.includes(n))) keys.push(k, ...vals);
    if (n === k || vals.includes(n)) keys.push(k, ...vals);
  }
  return [...new Set(keys)];
}

/** Does this risk belong to selected GeoJSON country name? */
export function riskInCountry(
  riskCountry: string | undefined,
  matchedCountry: string | undefined,
  geoCountryName: string
): boolean {
  const g = normalizeCountry(geoCountryName);
  const r = normalizeCountry(riskCountry);
  const m = normalizeCountry(matchedCountry);
  if (!g) return false;
  /* Substring match only for names long enough to be unambiguous; short ISO codes
     (e.g. "au") must resolve via matchedCountry (full name) or the aliases below,
     so "au" does not false-match "saudi arabia". */
  if (r && (r === g || (r.length >= 4 && (g.includes(r) || r.includes(g))))) return true;
  if (m && (m === g || (m.length >= 4 && (g.includes(m) || m.includes(g))))) return true;
  for (const keys of Object.values(ALIASES)) {
    if (keys.some((k) => normalizeCountry(k) === r || normalizeCountry(k) === m)) {
      if (keys.some((k) => g.includes(normalizeCountry(k)) || normalizeCountry(k).includes(g)))
        return true;
    }
  }
  return false;
}

/** Count of risk matches per GeoJSON feature name (best-effort) */
export function countMatchesPerGeoName(
  matches: { risk: { country?: string }; matchedCountry: string }[],
  geoFeatureNames: string[]
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const name of geoFeatureNames) counts.set(name, 0);
  for (const m of matches) {
    for (const geoName of geoFeatureNames) {
      if (riskInCountry(m.risk.country, m.matchedCountry, geoName)) {
        counts.set(geoName, (counts.get(geoName) || 0) + 1);
      }
    }
  }
  return counts;
}

export interface GeoOppRiskCounts {
  opportunities: number;
  risks: number;
}

function bumpOppRisk(
  map: Map<string, GeoOppRiskCounts>,
  geoName: string,
  isPositive: boolean | undefined | null
): void {
  const row = map.get(geoName) || { opportunities: 0, risks: 0 };
  if (isPositive === true) row.opportunities += 1;
  else if (isPositive === false) row.risks += 1;
  map.set(geoName, row);
}

/**
 * Per country: opportunities = is_positive === true, risks = is_positive === false.
 * Rows with is_positive null/undefined are not counted in either bucket (still on map, not in choropleth sum).
 */
export function countOppRiskPerGeoName(
  matches: { risk: { country?: string; is_positive?: boolean | null }; matchedCountry: string }[],
  geoFeatureNames: string[]
): Map<string, GeoOppRiskCounts> {
  const out = new Map<string, GeoOppRiskCounts>();
  for (const name of geoFeatureNames) out.set(name, { opportunities: 0, risks: 0 });
  for (const m of matches) {
    for (const geoName of geoFeatureNames) {
      if (riskInCountry(m.risk.country, m.matchedCountry, geoName)) {
        bumpOppRisk(out, geoName, m.risk.is_positive);
      }
    }
  }
  return out;
}

export function totalOppRisk(c: GeoOppRiskCounts): number {
  return c.opportunities + c.risks;
}
